import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../config/supabase';
import { setAuthToken } from '../services/api';

/**
 * User interface - represents authenticated user
 */
interface User {
  id: string;       // Unique user ID (UUID from Supabase)
  email?: string;   // User's email address
}

/**
 * AuthContext interface
 * */
interface AuthContextType {
  user: User | null;                                          
  loading: boolean;                                          
  signUp: (email: string, password: string) => Promise<void>; 
  signIn: (email: string, password: string) => Promise<void>; 
  signOut: () => Promise<void>;                               
  error: string | null;                                       
}

// Create context with undefined default (ensures provider is used)
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider Component
 * @param children - React components to wrap
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // State: Current user (null when logged out)
  const [user, setUser] = useState<User | null>(null);
  
  // State: Loading status (true while checking existing session)
  const [loading, setLoading] = useState(true);
  
  // State: Error messages from auth operations
  const [error, setError] = useState<string | null>(null);

  // Effect: Initialize auth on component mount
  useEffect(() => {
        const getSession = async () => {
      try {
        // Ask Supabase if there's an active session
        const {
          data: { session },
        } = await supabase.auth.getSession();
        
        if (session) {
          setUser({ id: session.user.id, email: session.user.email });
          
          setAuthToken(session.access_token);
          
          localStorage.setItem('supabase_token', session.access_token);
        } else {
          const token = localStorage.getItem('supabase_token');
          if (token) {
            setAuthToken(token);
          }
        }
      } catch (err) {
        console.error('Error getting session:', err);
      } finally {
        // Always stop loading, even if error occurred
        setLoading(false);
      }
    };
    
    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session) {
        // User signed in or session refreshed
        setUser({ id: session.user.id, email: session.user.email });
        setAuthToken(session.access_token);
        localStorage.setItem('supabase_token', session.access_token);
      } else {
        // User signed out or session expired
        setUser(null);
        setAuthToken(null);
        localStorage.removeItem('supabase_token');
      }
    });
    
    // Cleanup: Unsubscribe when component unmounts
    return () => {
      subscription.unsubscribe();
    };
  }, []);  

  /**
   * Sign up a new user
   * 
   * @param email
   * @param password
   * */
  const signUp = async (email: string, password: string) => {
    try {
      setError(null);  // Clear previous errors
      
      // Call Supabase to create new user
      const { data, error } = await supabase.auth.signUp({ email, password });
      
      if (error) throw error;
      
      if (data.session) {
        // Account confirmed immediately (no email verification required)
        setAuthToken(data.session.access_token);
        localStorage.setItem('supabase_token', data.session.access_token);
      } else {
        // Email verification required
        setError('Please check your email to confirm your account');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign up');
      console.error('Sign up error:', err);
    }
  };

  /**
   * Sign in an existing user
   * 
   * @param email - User's email address
   * @param password - User's password
   */
  const signIn = async (email: string, password: string) => {
    try {
      setError(null);  // Clear previous errors
      
      // Call Supabase to authenticate user
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) throw error;
      
      if (data.session) {
        // Successfully signed in, store token
        setAuthToken(data.session.access_token);
        localStorage.setItem('supabase_token', data.session.access_token);
        // Note: User state updated via onAuthStateChange listener
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign in');
      console.error('Sign in error:', err);
    }
  };

  /**
   * Sign out the current user
   */
  const signOut = async () => {
    try {
      setError(null);  // Clear previous errors
      
      // Call Supabase to sign out
      await supabase.auth.signOut();
      
      // Clear token from API client
      setAuthToken(null);
      
      // Clear token from localStorage
      localStorage.removeItem('supabase_token');
      
      // Note: User state cleared via onAuthStateChange listener
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign out');
      console.error('Sign out error:', err);
    }
  };

  // Provide auth state and functions to all children
  return (
    <AuthContext.Provider value={{ user, loading, signUp, signIn, signOut, error }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth Hook
 * 
 * Custom hook to access authentication context.
 * 
* @returns AuthContextType object with user, loading, signUp, signIn, signOut, error
 * @throws Error if used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  // Ensure hook is used within AuthProvider
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
